import { Box, Typography, Button, Avatar, Container } from '@mui/material';
import { PublicOwner } from '../../services/publicApi';

interface LandingPageHeroProps {
  owner: PublicOwner;
  headline: string;
  subheadline: string;
  primaryColor: string;
}

export const LandingPageHero = ({ owner, headline, subheadline, primaryColor }: LandingPageHeroProps) => {
  const fullName = `${owner.firstName} ${owner.lastName}`;
  const initials = `${owner.firstName.charAt(0)}${owner.lastName.charAt(0)}`.toUpperCase();

  const scrollToLeadMagnet = () => {
    const element = document.getElementById('lead-magnet');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        background: `linear-gradient(135deg, ${primaryColor}08 0%, ${primaryColor}15 100%)`,
        borderBottom: '1px solid',
        borderColor: 'grey.200',
      }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          {/* Avatar */}
          <Avatar
            src={owner.avatar}
            alt={fullName}
            sx={{
              width: 120,
              height: 120,
              mb: 3,
              bgcolor: owner.avatarColor || primaryColor,
              fontSize: '2.5rem',
              fontWeight: 600,
              border: '4px solid white',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            }}
          >
            {!owner.avatar && initials}
          </Avatar>

          {/* Name */}
          <Typography
            variant="h6"
            sx={{
              color: primaryColor,
              fontWeight: 600,
              mb: 2,
              letterSpacing: '0.5px',
            }}
          >
            {fullName}
          </Typography>

          {/* Headline */}
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '2rem', md: '3rem' },
              lineHeight: 1.2,
              color: 'grey.900',
            }}
          >
            {headline || 'Your Partner in Productivity'}
          </Typography>

          {/* Subheadline */}
          <Typography
            variant="h5"
            sx={{
              color: 'grey.600',
              mb: 4,
              maxWidth: 600,
              fontSize: { xs: '1.1rem', md: '1.25rem' },
              lineHeight: 1.6,
            }}
          >
            {subheadline || 'I help busy professionals reclaim their time and focus on what matters most.'}
          </Typography>

          {/* CTA Button */}
          <Button
            variant="contained"
            size="large"
            onClick={scrollToLeadMagnet}
            sx={{
              bgcolor: primaryColor,
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              boxShadow: `0 4px 14px ${primaryColor}40`,
              '&:hover': {
                bgcolor: primaryColor,
                filter: 'brightness(0.9)',
                boxShadow: `0 6px 20px ${primaryColor}50`,
              },
            }}
          >
            Get Your Free Action Plan
          </Button>
        </Box>
      </Container>
    </Box>
  );
};
